import { useCallback, useMemo, useState } from 'react';

import type { SortDirection } from '@/shared/ui';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { createComparator } from '@/shared/utils/sorting';

import { useDebounce } from '../useDebounce/useDebounce';
import type { UseTableDataOptions, UseTableDataReturn } from './useTableData.types';

/**
 * Generic hook for managing table data with search, sort, and pagination
 */
export function useTableData<TData, TTransformedData = TData>({
  data,
  transform,
  searchFields,
  searchFilter,
  initialSort,
  initialRowsPerPage = 10,
  debounceMs = SEARCH_DEBOUNCE_MS,
}: UseTableDataOptions<TData, TTransformedData>): UseTableDataReturn<TTransformedData> {
  const [searchValue, setSearchValueState] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, debounceMs);
  const [sortBy, setSortBy] = useState<keyof TTransformedData>(
    initialSort?.key ?? ('' as keyof TTransformedData),
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    initialSort?.direction ?? 'asc',
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Transform data if transform function is provided
  const transformedData = useMemo(() => {
    return transform ? transform(data) : (data as unknown as TTransformedData[]);
  }, [data, transform]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!debouncedSearchValue.trim()) {
      return transformedData;
    }

    const searchLower = debouncedSearchValue.toLowerCase();

    // Use custom search filter if provided
    if (searchFilter) {
      return transformedData.filter((item) => searchFilter(item, searchLower));
    }

    // Use default search behavior with searchFields
    if (searchFields && searchFields.length > 0) {
      return transformedData.filter((item) => {
        return searchFields.some((field) => {
          const value = item[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchLower);
        });
      });
    }

    // No search configured, return all data
    return transformedData;
  }, [transformedData, debouncedSearchValue, searchFields, searchFilter]);

  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData;

    return [...filteredData].sort(createComparator(sortBy, sortDirection));
  }, [filteredData, sortBy, sortDirection]);

  // Removing the last row of the final page would otherwise strand the reader on an empty page
  // with no pagination to leave it. Corrected during render so the empty page never paints.
  const lastPage = Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1);
  if (page > lastPage) {
    setPage(lastPage);
  }

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, page, rowsPerPage]);

  const setSearchValue = useCallback((value: string) => {
    setSearchValueState(value);
    setPage(0);
  }, []);

  const handleSort = useCallback((key: keyof TTransformedData, direction: SortDirection) => {
    setSortBy(key);
    setSortDirection(direction);
    setPage(0);
  }, []);

  /** Toggles sort direction for the given key. Compatible with Table's onSort signature. */
  const toggleSort = useCallback(
    (key: keyof TTransformedData) => {
      // eslint-disable-next-line i18next/no-literal-string -- sort direction values
      const nextDirection = sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortBy(key);
      setSortDirection(nextDirection);
      setPage(0);
    },
    [sortBy, sortDirection],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
  }, []);

  return {
    paginatedData,
    filteredData: sortedData,
    totalCount: sortedData.length,
    searchValue,
    debouncedSearchValue,
    setSearchValue,
    sortBy,
    sortDirection,
    handleSort,
    toggleSort,
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  };
}
